import React from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import ELID from 'constants/elid.const';
import S from 'constants/string.const';
import asArray from 'utils/arrays/asArray.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { formatPartDate, formatPartTime } from 'utils/date/format.util';
import focusElement from 'utils/dom/focusElement.util';
import asObject from 'utils/objects/asObject.util';
import deRef from 'utils/objects/deRef.util';
import parseDate from 'utils/parsers/parseDate';

import CN from './DepositRequesModal.module.scss';

const RE_TWO_DECIMALS_ONLY = /([^.]*.\d{0,2})(.*)/;
const LINK = 'http://help.getslick.com/en/articles/4664387-take-upfront-deposits-via-slick-pay-to-stop-no-shows';

class DepositRequesModal extends React.Component {
  depositInputRef = React.createRef();
  inputCustomRef = React.createRef();
  inputHalfRef = React.createRef();

  constructor(props) {
    super(props);

    this.state = {
      deposit: null,
      titleMessage: 'Your client will receive the following text message:',
      cardOnly: false,
      totalPrice: 0,
      value: 'half-value',
      customValueError: '',
    };

    this.depositInputRef = React.createRef();
    this.inputCustomRef = React.createRef();
  }

  componentDidMount() {
    setTimeout(() => {
      const element = document.getElementsByClassName('form-request')[0].children[1].children[1];

      if (element && element.value) {
        this.setState({ deposit: String(element.value) });
      }

      const procedures = asArray(this.props?.appointment?.procedures);
      let totalPrice = 0;
      for (let i = 0; i < procedures.length; i += 1) {
        totalPrice += procedures[i].price;
      }

      const fixedTotal = parseFloat(totalPrice.toFixed(2));
      this.setState({ totalPrice: fixedTotal, deposit: parseFloat(totalPrice / 2).toFixed(2) });
    }, 1000);
  }

  handleCustomValueChange = (ev) => {
    const { totalPrice } = this.state;

    const inputString = fromAnyToString(fromEventToValue(ev));
    const inputNumber = fromAnyToNumber(inputString) || 0;

    if (inputNumber < 1) {
      this.setState({
        customValueError: (
          <div>
            Minimum value must be at least <CurrencySign value="1.00" />
          </div>
        ),
        deposit: inputString,
      });
      return;
    }

    if (inputNumber > totalPrice) {
      return;
    }

    const deposit = inputString.replace(RE_TWO_DECIMALS_ONLY, '$1');

    this.setState({ deposit, value: 'custom-value', cardOnly: false, customValueError: '' });
  };

  handleCustomValueClick = () => void this.setState({ value: 'custom-value', deposit: '' });

  handleSend = () => {
    const { onDepositRequest } = this.props;
    const { cardOnly, deposit } = this.state;

    onDepositRequest(parseFloat(deposit), cardOnly);
  };

  handleRadio = (ev) => {
    const { totalPrice } = this.state;

    const customValue = deRef(this.inputCustomRef);
    const halfValue = deRef(this.inputHalfRef);

    const value = fromEventToValue(ev);
    const isHalfValue = value === 'half-value';
    const isCustomValue = value === 'custom-value';

    this.setState({ value });

    if (isHalfValue && halfValue) {
      focusElement(halfValue);
    }
    if (isCustomValue && customValue) {
      customValue.focus();
    }

    const deposit = parseFloat(totalPrice / 2).toFixed(2);

    this.setState({ deposit: isHalfValue ? deposit : S.empty });
    this.setState({ deposit: isCustomValue ? S.empty : deposit });
  };

  render() {
    const { value, totalPrice, deposit, titleMessage, customValueError } = this.state;
    const { onRequestClose, procedure, userName, clientHasCard, salonName } = this.props;
    const allProcedures = asArray(this.props?.appointment?.procedures);
    const firstStartTime = allProcedures.length > 0 ? allProcedures[0].start_time : null;
    const startTimeIso = firstStartTime || asObject(procedure).start_time;

    const startTimeNum = parseDate(startTimeIso);
    const displayDate = formatPartDate(startTimeNum);
    const displayTime = formatPartTime(startTimeNum);

    const isHalfValue = value === 'half-value';
    const isCustomValue = value === 'custom-value';

    const displayHalfValue = isHalfValue ? totalPrice / 2 : '';
    const displayCustomValue = isCustomValue ? deposit : '';
    const displayDeposit = deposit || totalPrice / 2;

    return (
      <div
        data-bem="DepositRequesModal"
        className={cn({
          [CN.component]: true,
          'dialog-container no-show': true,
        })}
      >
        <div data-bem="DepositRequesModal__dialog" className={CN.dialog}>
          <div data-bem="DepositRequesModal__modal" className="request-deposit-modal">
            <div
              data-bem="DepositRequesModal__close"
              role="button"
              tabIndex={0}
              className="close-button ico-close"
              onClick={onRequestClose}
            />
            <div className={CN.header}>
              <div className={CN.title}>Request a deposit payment</div>
            </div>
            <div data-bem="DepositRequesModal__body" className="form-body">
              <div className="form-request">
                <div className="total-price">
                  <Space>Total booking price:</Space>
                  <span>
                    <CurrencySign value={totalPrice} />
                  </span>
                </div>
                <div className="row radio-group cb-group-code">
                  <div className="row-container">
                    <label className="radio-btn" htmlFor="half-value">
                      <input
                        id="half-value"
                        value="half-value"
                        name="half-value"
                        type="radio"
                        checked={isHalfValue}
                        onChange={this.handleRadio}
                        onClick={this.handleRadio}
                      />
                      <span className="checkmark" />
                      <div className="radio-text">Request half upfront:</div>
                    </label>
                    <span className="pound">
                      <CurrencySign />
                    </span>
                    <input
                      id="half-value-input"
                      value={displayHalfValue}
                      type="text"
                      onClick={() => void this.setState({ value: 'half-value' })}
                      ref={this.inputHalfRef}
                      readOnly
                    />
                  </div>

                  <div className="row-container">
                    <label className="radio-btn" htmlFor="custom-value">
                      <input
                        id="custom-value"
                        value="custom-value"
                        name="custom-value"
                        type="radio"
                        checked={isCustomValue}
                        onChange={this.handleRadio}
                        onClick={this.handleRadio}
                      />
                      <span className="checkmark" />
                      <div className="radio-text">Request custom deposit:</div>
                    </label>

                    <span className="pound">
                      <CurrencySign />
                    </span>

                    <input
                      id="custom-value-input"
                      type="number"
                      onClick={this.handleCustomValueClick}
                      onChange={this.handleCustomValueChange}
                      value={displayCustomValue}
                      ref={this.inputCustomRef}
                    />
                  </div>
                </div>
              </div>

              <div className="request-message">
                <div>
                  <div className="text-violet">{titleMessage}</div>
                  <br />
                  <div>
                    <Space>
                      Hi {userName}! {salonName} has requested a deposit payment of
                    </Space>

                    <span className="fixed-width">
                      <CurrencySign value={displayDeposit} />
                    </span>

                    <Space>
                      to confirm your appointment on {displayDate} at {displayTime}.
                    </Space>

                    {clientHasCard ? (
                      <Space>Please login to your Slick account and pay the deposit via this link</Space>
                    ) : (
                      <Space>Please create a Slick account and pay the deposit via this link</Space>
                    )}
                  </div>
                </div>
                <div />
              </div>
            </div>

            <div
              data-bem="DepositRequesModal__footer"
              data-find-me-as={ELID.findMeAsFormFooter}
              className={cn({
                [CN.footer]: true,
                // NOTE: GS-GS-14360 DO not delete this class , make a date attribute or selector for if. very carefully there
                // check SchedulerMapped line ~ 890
                'form-footer': true,
              })}
            >
              {customValueError && <div className={CN.error}>{customValueError}</div>}
              <div className={CN.flex}>
                <ExternalLink variant="inline" link={LINK}>
                  <div className={CN.linkContainer}>
                    <Icon variant="info" width="lg" />
                    <div className={CN.link}>Learn more about how this works</div>
                  </div>
                </ExternalLink>

                <Button variant="primaryForce" onClick={this.handleSend} disabled={!deposit || customValueError}>
                  SEND REQUEST
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DepositRequesModal;
